// components/CitySelect.tsx
import React from "react";
import MasterDropdown from "../components/common/MasterDropdown";
import { useMasterDropdown } from "../hooks/useMasterDropdown";
import { masterDropdownService } from "../services/masterDropdownService";

interface CitySelectProps {
  state?: string | number; // State name (string) or state ID (number) for backward compatibility
  value: string | number | null | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStateChange?: (stateName: string, stateId: number) => void;
  required?: boolean;
  label?: string;
  name?: string;
  disableClearable?: boolean;
}

const CitySelect: React.FC<CitySelectProps> = ({
  state,
  value,
  onChange,
  onStateChange,
  required = false,
  label = "City",
  name = "city",
  disableClearable
}) => {
  // Use stateName if state is a string, otherwise use stateId for backward compatibility
  const { options, loading } = useMasterDropdown({
    masterType: "cities",
    stateName: typeof state === 'string' ? state : undefined,
    stateId: typeof state === 'number' ? state : undefined,
    enabled: true,
  });

  const handleChange = async (newValue: string | number | null) => {
    // Mimic old event structure for backward compatibility
    const fakeEvent = {
      target: {
        name,
        value: newValue || "",
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(fakeEvent);

    // If a city is selected, set the state automatically
    if (newValue && onStateChange) {
      // Try to find the selected city in current options
      // Handle both string and number comparisons for robustness
      const selectedOption = options.find(opt => {
        return String(opt.value) === String(newValue) || opt.value === newValue;
      });
      
      // If found in options and has state info, use it
      if (selectedOption && selectedOption.stateName) {
        console.log('City selected, auto-setting state:', {
          city: selectedOption.label,
          state: selectedOption.stateName,
          stateId: selectedOption.stateId
        });
        onStateChange(selectedOption.stateName, selectedOption.stateId || 0);
      } else {
        // Fallback: fetch state from API if not found in options or state info is missing
        console.log('State info not found in options, fetching from API for city:', newValue);
        try {
          const stateInfo = await masterDropdownService.getStateByCity(newValue);
          if (stateInfo && stateInfo.stateName) {
            console.log('City selected, auto-setting state (from API):', {
              city: newValue,
              state: stateInfo.stateName,
              stateId: stateInfo.stateId
            });
            onStateChange(stateInfo.stateName, stateInfo.stateId || 0);
          } else {
            console.warn('Could not find state for selected city:', newValue, {
              optionsCount: options.length,
              selectedOption,
              stateInfo
            });
          }
        } catch (error) {
          console.error('Error fetching state by city from API:', error);
          console.warn('Could not find state for selected city:', newValue, {
            optionsCount: options.length,
            selectedOption
          });
        }
      }
    } else if (!newValue && onStateChange) {
      // If city is cleared, clear the state as well
      onStateChange("", 0);
    }
  };

  return (
    <MasterDropdown
      options={options}
      value={value}
      onChange={handleChange}
      loading={loading}
      label={label}
      name={name}
      required={required}
      disableClearable={false}
      fullWidth
    />
  );
};

export default CitySelect;
